import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { downloadFile } from "@/api/client";
import { PageContainer } from "@/components/PageContainer";
import { PageHeader } from "@/components/PageHeader";
import { apiErrorMessage } from "@/lib/apiError";
import { ReservePanel } from "./ReservePanel";
import { TimelineChart } from "./TimelineChart";

export function BudgetPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  // Exports cannot be plain `<a href>` links: a browser navigation can't send
  // the in-memory Bearer token, so the Bearer-only API returns 401. Fetch the
  // file with the token via the api client and download it from a blob URL.
  async function handleExport(fmt: "xlsx" | "pdf" | "npk"): Promise<void> {
    try {
      await downloadFile(`/objects/${id}/export/${fmt}`, `export.${fmt}`);
    } catch (err) {
      window.alert(apiErrorMessage(err, t("export.failed")));
    }
  }

  if (!id)
    return (
      <PageContainer width="wide">
        <p className="text-negative">{t("common.error")}</p>
      </PageContainer>
    );

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("budget.title")}
        actions={
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="self-center text-ink-muted">{t("export.label")}:</span>
            <button
              type="button"
              onClick={() => void handleExport("xlsx")}
              className="rounded border border-rule bg-paper-raised px-3 py-1 hover:bg-paper-sunk"
            >
              {t("export.xlsx")}
            </button>
            <button
              type="button"
              onClick={() => void handleExport("pdf")}
              className="rounded border border-rule bg-paper-raised px-3 py-1 hover:bg-paper-sunk"
            >
              {t("export.pdf")}
            </button>
            <button
              type="button"
              onClick={() => void handleExport("npk")}
              className="rounded border border-rule bg-paper-raised px-3 py-1 hover:bg-paper-sunk"
            >
              {t("export.npk")}
            </button>
          </div>
        }
      />

      <div className="space-y-6">
        <ReservePanel objectId={id} />

        <div className="rounded border border-rule bg-paper-raised p-4">
          <TimelineChart objectId={id} />
        </div>
      </div>
    </PageContainer>
  );
}
